import React from "react";
import { Box } from "@mui/material";

/**
 * Semicircle score gauge (mobile) — mobile equivalent of desktop's
 * `components/Gauge/Meter`, rebuilt as inline SVG.
 *
 * Acts as a semicircle progress bar: a light-grey track spans the whole arc, and
 * the colour fills from 0 up to the score, revealing the three bands (red 0–50 /
 * cream 50–70 / green 70–100) only up to that point. The fill animates in on
 * load. Bands map to fixed thirds of the arc (desktop's `valueToDegree`), so the
 * 50/70 breakpoints sit symmetrically at the top. No needle — the number carries
 * the exact value.
 *
 * @param {number} value        0–100
 * @param {number} [breakpoint1] first band edge (default 50)
 * @param {number} [breakpoint2] second band edge (default 70)
 */

const BAND_DEFS = [
  { color: "#F0928A", from: 0, to: 50 }, // red
  { color: "#FBEFD6", from: 50, to: 70 }, // cream
  { color: "#0FA06E", from: 70, to: 100 }, // green
];

export default function ScoreGauge({
  value = 0,
  breakpoint1 = 50,
  breakpoint2 = 70,
}) {
  const W = 240;
  const H = 150;
  const cx = W / 2;
  const cy = 118;
  const r = 76; // centreline radius
  const stroke = 22; // band thickness
  const outer = r + stroke / 2;
  const inner = r - stroke / 2;

  const TRACK = "#E9EDF2";
  const BORDER = "#CCD4DE";
  const clamped = Math.min(Math.max(value, 0), 100);

  // Animate the coloured fill from 0 up to the score on load.
  const [fill, setFill] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const start = performance.now();
    const duration = 1000;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setFill(clamped * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [clamped]);

  const arcValue = (rad, v1, v2) =>
    arcPath(
      cx,
      cy,
      rad,
      valueToTheta(v1, breakpoint1, breakpoint2),
      valueToTheta(v2, breakpoint1, breakpoint2)
    );

  const thetaBp1 = valueToTheta(breakpoint1, breakpoint1, breakpoint2);
  const thetaBp2 = valueToTheta(breakpoint2, breakpoint1, breakpoint2);

  const leftInner = polar(cx, cy, inner, 180);
  const leftOuter = polar(cx, cy, outer, 180);
  const rightInner = polar(cx, cy, inner, 0);
  const rightOuter = polar(cx, cy, outer, 0);

  return (
    <Box sx={{ width: "100%", maxWidth: W, mx: "auto" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`Score ${Math.round(clamped)} out of 100`}
      >
        {/* Grey track */}
        <path d={arcPath(cx, cy, r, 180, 0)} fill="none" stroke={TRACK} strokeWidth={stroke} />

        {/* Coloured fill up to the (animating) score */}
        {BAND_DEFS.map((b) =>
          fill > b.from ? (
            <path
              key={b.color}
              d={arcValue(r, b.from, Math.min(b.to, fill))}
              fill="none"
              stroke={b.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
            />
          ) : null
        )}

        {/* Band border — outer + inner edges and the two flat ends */}
        <path d={arcPath(cx, cy, outer, 180, 0)} fill="none" stroke={BORDER} strokeWidth={1.25} />
        <path d={arcPath(cx, cy, inner, 180, 0)} fill="none" stroke={BORDER} strokeWidth={1.25} />
        <line x1={leftInner.x} y1={leftInner.y} x2={leftOuter.x} y2={leftOuter.y} stroke={BORDER} strokeWidth={1.25} />
        <line x1={rightInner.x} y1={rightInner.y} x2={rightOuter.x} y2={rightOuter.y} stroke={BORDER} strokeWidth={1.25} />

        {/* Breakpoint labels above the arc boundaries */}
        <GaugeLabel cx={cx} cy={cy} r={outer + 16} theta={thetaBp1} text={String(breakpoint1)} />
        <GaugeLabel cx={cx} cy={cy} r={outer + 16} theta={thetaBp2} text={String(breakpoint2)} />

        {/* Centre value */}
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="34" fontWeight="700" fill="#1A1A1A">
          {Math.round(clamped)}
        </text>

        {/* Baseline row: 0 | /100 | 100 */}
        <text x={cx - r} y={cy + 20} textAnchor="middle" fontSize="13" fill="#636F7F">
          0
        </text>
        <text x={cx} y={cy + 20} textAnchor="middle" fontSize="13" fill="#636F7F">
          /100
        </text>
        <text x={cx + r} y={cy + 20} textAnchor="middle" fontSize="13" fill="#636F7F">
          100
        </text>
      </svg>
    </Box>
  );
}

function GaugeLabel({ cx, cy, r, theta, text }) {
  const p = polar(cx, cy, r, theta);
  return (
    <text
      x={p.x}
      y={p.y}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize="17"
      fontWeight="500"
      fill="#45505F"
    >
      {text}
    </text>
  );
}

// Desktop's mapping: each band gets a fixed 60° of the 180° arc, then converted
// to a standard math angle (0° = right, 90° = top, 180° = left).
function valueToDegree(v, bp1, bp2) {
  const startAngle = 60;
  const endAngle = 120;
  if (v <= bp1) return (v / bp1) * startAngle;
  if (v <= bp2) return startAngle + ((v - bp1) / (bp2 - bp1)) * (endAngle - startAngle);
  return endAngle + ((v - bp2) / (100 - bp2)) * (180 - endAngle);
}

function valueToTheta(v, bp1, bp2) {
  return 180 - valueToDegree(v, bp1, bp2);
}

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

function arcPath(cx, cy, r, thetaFrom, thetaTo) {
  const a = polar(cx, cy, r, thetaFrom);
  const b = polar(cx, cy, r, thetaTo);
  const largeArc = Math.abs(thetaFrom - thetaTo) > 180 ? 1 : 0;
  // Sweeps clockwise (decreasing angle, over the top).
  return `M ${a.x} ${a.y} A ${r} ${r} 0 ${largeArc} 1 ${b.x} ${b.y}`;
}
